import {
  AbilityBuilder,
  createMongoAbility,
  MongoAbility,
} from "@casl/ability";
import { OrganizationMembershipResource } from "@clerk/types";

import { Subjects } from "./subjects";

type Actions = "create" | "read" | "update" | "delete" | "manage";

export type AppAbility = MongoAbility<[Actions, Subjects]>;

export function defineAbilityFor(
  attributes: {
    role: OrganizationMembershipResource["role"] | null;
    locations: string[] | null;
    isSystemAdminOrg: boolean;
  } | null,
) {
  const {
    can: allow,
    cannot: forbid,
    build,
  } = new AbilityBuilder<AppAbility>(createMongoAbility);

  if (!attributes) {
    return build();
  }

  const { role, locations, isSystemAdminOrg } = attributes;

  if (role === "org:front_desk") {
    // Front desk can only read resources from locations that the user has access to
    allow("read", "SingleLocationView", {
      id: { $in: locations },
    });

    // Front desk can only read bookings from locations that the user has access to
    allow("read", "Booking", {
      "location.id": { $in: locations },
    });

    // Front desk can only update following fields on booking:
    allow("update", "Booking", ["group.guests.arrived"]);
    allow("update", "Booking", ["paymentCategory"]);
    allow("update", "Booking", ["paymentCategoryDrinks"]);
    allow("update", "Booking", ["group.nationalities"]);
    allow("update", "Booking", ["internalInformation"]);
    allow("update", "Booking", ["program.quantity"]);
    allow("update", "Booking", ["group.arrivals.adults"]);
    allow("update", "Booking", ["group.arrivals.staff"]);
    allow("update", "Booking", ["group.arrivals.childrenFree"]);
    allow("update", "Booking", ["group.arrivals.childrenPaid"]);

    allow("read", "Department");
    allow("read", "Calendar");
    allow("read", "Product");
    allow("read", "ProductCategory");
    allow("read", "Employee");
    allow("read", "Customer");
  }

  if (role === "org:guide") {
    // Guide can only read resources from locations that the user has access to
    allow("read", "SingleLocationView", {
      id: { $in: locations },
    });

    // Guide can only read bookings from locations that the user has access to
    allow("read", "Booking", {
      "location.id": { $in: locations },
    });

    allow("read", "Department");
    allow("read", "Calendar");
    allow("read", "Product");
    allow("read", "ProductCategory");
    allow("read", "Employee");
    allow("read", "Customer");
    allow("read", "Report");
  }

  if (role === "org:manager") {
    allow("manage", "all");
    // Manager cannot access multi-location resources
    forbid("manage", "MultiLocationView");
    // Manager can only read resources from locations that the user has access to
    forbid("manage", "SingleLocationView", {
      id: { $nin: locations },
    });
    // Manager can only read bookings from locations that the user has access to
    forbid("read", "Booking", {
      "location.id": { $nin: locations },
    });
  }

  forbid("update", "Booking", ["sentToPowerOfficeDate"]);

  if (role === "org:admin") {
    allow("manage", "all");
    // Admin cannot access multi-location resources, unless there are in system admin organization
    if (!isSystemAdminOrg) forbid("manage", "MultiLocationView");

    // Admin can only read resources from locations that the user has access to
    forbid("manage", "SingleLocationView", {
      id: { $nin: locations },
    });
    // Admin can only read bookings from locations that the user has access to
    forbid("read", "Booking", {
      "location.id": { $nin: locations },
    });
  }

  // Noone can update booking that has been sent
  forbid("update", "Booking", {
    sentToPowerOfficeDate: { $ne: null },
  });

  forbid("update", "Booking", "sentToPowerOfficeDate", {
    status: { $eq: "cancelled" },
  });

  forbid("update", "Booking", "sentToPowerOfficeDate", {
    status: { $eq: "sentforinvoicing" },
  });

  return build();
}
